import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'

import { Button, Kind } from '@thanx/uikit/button'
import { Checkbox } from '@thanx/uikit/checkbox'
import { Modal } from '@thanx/uikit/modal'
import { Radio } from '@thanx/uikit/radio'
import { useStyletron } from '@thanx/uikit/theme'

import { alert } from 'actions/flash'
import { useGetGrantedRewardImpactPreviewQuery } from 'api/grantedRewardExpiration'
import { useUpdateMerchantMutation } from 'api/merchants'
import palette from 'constants/palette'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import CmsPage from 'scenes/Cms/components/CmsPage'
import { selectCurrentMerchant } from 'selectors/merchant'
import { useAbility } from 'utilities/ability'

const SECTION_TITLE_ID = 'reward-expiration-section-title'
const IMPACT_CALLOUT_LABEL_ID = 'reward-expiration-impact-callout-label'
const CONFIRM_MODAL_TITLE_ID = 'reward-expiration-confirm-modal-title'
const DAYS_UNIT_ID = 'reward-expiration-days-unit'
const DAYS_ERROR_ID = 'reward-expiration-days-error'
// Intentional deviations from palette.
const AMBER_CALLOUT_BG = '#FAF0D1'
const PUBLISH_BANNER_BG = '#EBF5FB'
const INPUT_GROUP_BG = '#F4F6F8'

// Widen uikit prop types so aria-labelledby can be threaded through without
// an @ts-ignore (the uikit prop aliases aren't exported for augmentation).
const AccessibleCheckbox = Checkbox as React.ComponentType<
  React.ComponentProps<typeof Checkbox> & { 'aria-labelledby'?: string }
>
const AccessibleModal = Modal as React.ComponentType<
  React.ComponentProps<typeof Modal> & { 'aria-labelledby'?: string }
>

const t = buildTranslate('cms.settings.reward_expiration')
const publishBannerT = buildTranslate('cms.components.publish_banner')

const DEFAULT_DAYS = 30

// null = no selection made (neither radio checked).
type ExpirationMode = 'no_expiration' | 'expires_after' | null

type ImpactCalloutProps = {
  checked: boolean
  onChange: (checked: boolean) => void
  isPreviewLoading: boolean
  hasPreview: boolean
  isPreviewError: boolean
  formattedCount: string
}

const ImpactCallout = ({
  checked,
  onChange,
  isPreviewLoading,
  hasPreview,
  isPreviewError,
  formattedCount,
}: ImpactCalloutProps) => {
  const [css] = useStyletron()
  return (
    <div
      className={`mt-m ${css({
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '16px',
        backgroundColor: AMBER_CALLOUT_BG,
        border: `1px solid ${palette.grey30}`,
        borderRadius: '4px',
      })}`}
    >
      <AccessibleCheckbox
        checked={checked}
        onChange={onChange}
        aria-labelledby={IMPACT_CALLOUT_LABEL_ID}
      />
      <div className={css({ flex: '1 1 auto' })}>
        <p
          id={IMPACT_CALLOUT_LABEL_ID}
          className={`body-text-4 m-0 ${css({
            color: palette.grey90,
            fontWeight: 700,
          })}`}
        >
          {t('rich_callout.title')}
        </p>
        <p
          role="status"
          aria-live="polite"
          className={`body-text-4 mt-xs mb-0 ${css({
            color: palette.grey70,
          })}`}
        >
          {isPreviewError ? (
            <span>{t('impact_preview.error')}</span>
          ) : isPreviewLoading || !hasPreview ? (
            <span>{t('impact_preview.loading')}</span>
          ) : (
            // eslint-disable-next-line react/no-danger
            <span
              dangerouslySetInnerHTML={{
                __html: t('rich_callout.body', {
                  rewardsCount: formattedCount,
                }),
              }}
            />
          )}
        </p>
      </div>
    </div>
  )
}

type ConfirmPublishModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isSubmitting: boolean
  formattedCount: string
  days: number
}

const ConfirmPublishModal = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
  formattedCount,
  days,
}: ConfirmPublishModalProps) => {
  const [css] = useStyletron()
  return (
    <AccessibleModal
      isOpen={isOpen}
      onClose={onClose}
      size="auto"
      closeType="small"
      zIndex={1200}
      aria-labelledby={CONFIRM_MODAL_TITLE_ID}
      overrides={{
        Dialog: {
          style: {
            width: '600px',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
          },
        },
      }}
    >
      <div className={css({ padding: '48px' })}>
        <h3
          id={CONFIRM_MODAL_TITLE_ID}
          className={`m-0 ${css({ whiteSpace: 'nowrap' })}`}
        >
          {t('confirm_modal.title')}
        </h3>
        {/* eslint-disable-next-line react/no-danger */}
        <p
          className={`body-text-3 mt-s mb-0 ${css({ color: palette.grey70 })}`}
          dangerouslySetInnerHTML={{
            __html: t('confirm_modal.body', {
              rewardsCount: formattedCount,
              days,
            }),
          }}
        />
        <div className={`mt-l d-flex align-items-start ${css({ gap: '8px' })}`}>
          <i
            aria-hidden
            className={`fa fa-exclamation-triangle ${css({
              color: palette.eggYolk50,
              fontSize: '20px',
              lineHeight: '20px',
              marginTop: '0',
            })}`}
          />
          <p className={`body-text-4 m-0 ${css({ color: palette.grey70 })}`}>
            {t('confirm_modal.warning')}
          </p>
        </div>
        <div
          className={`mt-xl d-flex justify-content-end ${css({
            gap: '16px',
          })}`}
        >
          <Button kind={Kind.SECONDARY} onClick={onClose}>
            {t('confirm_modal.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onConfirm}>
            {t('confirm_modal.confirm')}
          </Button>
        </div>
      </div>
    </AccessibleModal>
  )
}

const RewardExpiration = ({ showTitle = true }: { showTitle?: boolean }) => {
  const [css] = useStyletron()
  const flagEnabled = useFlag('mx-granted-reward-expiration-settings', false)
  // `access` reaches the page; `manage` submits the form.
  const ability = useAbility()
  const canViewMerchantSettings = ability.can('access', 'MerchantSettings')
  const canManageMerchantSettings = ability.can('manage', 'MerchantSettings')
  const dispatch = useDispatch()
  const merchant = useSelector(selectCurrentMerchant)

  const [updateMerchant, { isLoading: isSubmitting }] =
    useUpdateMerchantMutation()

  const persistedDays = merchant?.granted_reward_retire_after_days
  const persistedSet = merchant?.granted_reward_retire_after_days_set
  // Tri-state: set=false → no selection; set=true + days=null → "No
  // expiration"; set=true + days 1..999 → "Expires after N days".
  const initialMode: ExpirationMode = !persistedSet
    ? null
    : persistedDays != null && persistedDays > 0
    ? 'expires_after'
    : 'no_expiration'
  const initialDays =
    persistedDays && persistedDays > 0 ? persistedDays : DEFAULT_DAYS

  const [mode, setMode] = useState<ExpirationMode>(initialMode)
  const [days, setDays] = useState<number>(initialDays)
  const [applyToGranted, setApplyToGranted] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    setMode(initialMode)
    setDays(initialDays)
    setApplyToGranted(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedDays, persistedSet])

  const selectMode = (next: ExpirationMode) => {
    setMode(next)
    // The opt-in only applies to "Expires after"; reset it on mode change.
    setApplyToGranted(false)
  }

  const skipPreview =
    !flagEnabled ||
    !canViewMerchantSettings ||
    !merchant?.id ||
    mode !== 'expires_after'

  const previewQueryOptions = useMemo(
    () => ({ skip: skipPreview }),
    [skipPreview]
  )
  const {
    data: preview,
    isFetching: isPreviewLoading,
    isError: isPreviewError,
    refetch: refetchPreview,
  } = useGetGrantedRewardImpactPreviewQuery(undefined, previewQueryOptions)

  // Hidden when the preview confirms zero outstanding rewards; kept visible
  // while loading/unfetched so the loading state renders.
  const showRichCallout =
    mode === 'expires_after' && preview?.rewardsCount !== 0

  useEffect(() => {
    if (!showRichCallout) {
      setApplyToGranted(false)
    }
  }, [showRichCallout])

  const displayAlert = useCallback(
    (key: 'danger' | 'success', messageKey: string) => {
      dispatch(
        alert({
          key,
          message: t(messageKey),
          timeout: 5,
        })
      )
    },
    [dispatch]
  )

  const targetValue: number | null =
    mode === 'no_expiration' ? null : mode === 'expires_after' ? days : null
  const hasChanges =
    mode !== null &&
    (targetValue !== persistedDays || !persistedSet || applyToGranted)
  // max={999} is only a visual hint; enforce the upper bound here too.
  const daysWithinBounds = Number.isInteger(days) && days >= 1 && days <= 999
  const isValid =
    mode === 'no_expiration' || (mode === 'expires_after' && daysWithinBounds)
  const showDaysError = mode === 'expires_after' && !isValid

  const submit = useCallback(async () => {
    try {
      await updateMerchant({
        data: {
          granted_reward_retire_after_days: targetValue,
          apply_to_granted_rewards: applyToGranted,
        },
      }).unwrap()
      displayAlert('success', 'submit_success')
      // Only close on success — on error keep the modal open to retry.
      setIsModalOpen(false)
      // The persisted expiration value may be unchanged (e.g. only the
      // retroactive opt-in was sent), so the [persistedDays, persistedSet]
      // reset effect won't fire. Clear the opt-in and refetch the preview
      // here so it can't be re-confirmed against a now-stale count.
      setApplyToGranted(false)
      // Guard: refetch() throws on a query that was never started (skipped).
      if (!skipPreview) refetchPreview()
    } catch (error) {
      displayAlert('danger', 'submit_error')
    }
  }, [
    updateMerchant,
    targetValue,
    applyToGranted,
    displayAlert,
    refetchPreview,
    skipPreview,
  ])

  const handlePublishClick = useCallback(() => {
    if (applyToGranted) {
      setIsModalOpen(true)
    } else {
      submit()
    }
  }, [applyToGranted, submit])

  const closeModal = useCallback(() => setIsModalOpen(false), [])

  const formattedCount = preview?.rewardsCount?.toLocaleString() ?? ''

  // Gate placed after all hooks to keep hook order stable.
  if (!flagEnabled || !canViewMerchantSettings) {
    return <Redirect to="/" />
  }

  return (
    <>
      <CmsPage
        showTitle={showTitle}
        title={t('title')}
        description={t('description')}
      >
        <div className="mb-m">
          <h4
            id={SECTION_TITLE_ID}
            className={`mt-0 mb-xxs ${css({ color: palette.grey90 })}`}
          >
            {t('section_title')}
          </h4>
          <p className={`body-text-3 mb-m ${css({ color: palette.grey70 })}`}>
            {t('section_description')}
          </p>

          <div role="radiogroup" aria-labelledby={SECTION_TITLE_ID}>
            <Radio
              value="no_expiration"
              checked={mode === 'no_expiration'}
              onChange={() => selectMode('no_expiration')}
            >
              <span className={css({ color: palette.grey90 })}>
                {t('options.no_expiration.title')}
              </span>
            </Radio>

            <div
              className={`mt-s d-flex align-items-center ${css({
                gap: '12px',
              })}`}
            >
              <Radio
                value="expires_after"
                checked={mode === 'expires_after'}
                onChange={() => selectMode('expires_after')}
              >
                <span className={css({ color: palette.grey90 })}>
                  {t('options.expires_after.title')}
                </span>
              </Radio>
              <div
                className={css({
                  display: 'inline-flex',
                  alignItems: 'stretch',
                  border: `1px solid ${
                    showDaysError ? palette.cayenne : palette.grey40
                  }`,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  opacity: mode === 'expires_after' ? 1 : 0.6,
                })}
              >
                <input
                  type="number"
                  aria-label={t('options.expires_after.input_aria_label')}
                  aria-describedby={
                    showDaysError
                      ? `${DAYS_UNIT_ID} ${DAYS_ERROR_ID}`
                      : DAYS_UNIT_ID
                  }
                  aria-invalid={showDaysError}
                  min={1}
                  max={999}
                  step={1}
                  value={days}
                  disabled={mode !== 'expires_after'}
                  onChange={e => {
                    // Days are whole numbers; truncate explicitly so decimal
                    // input (autofill/programmatic) can't slip through as a
                    // silently-rounded value.
                    const next = Math.trunc(Number(e.target.value))
                    setDays(Number.isFinite(next) ? next : 0)
                  }}
                  className={css({
                    width: '70px',
                    padding: '8px 12px',
                    border: 'none',
                    outline: 'none',
                    fontSize: '14px',
                    color: palette.grey90,
                    backgroundColor: palette.white,
                  })}
                />
                <span
                  id={DAYS_UNIT_ID}
                  className={css({
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    borderLeft: `1px solid ${palette.grey40}`,
                    backgroundColor: INPUT_GROUP_BG,
                    color: palette.grey70,
                    fontSize: '14px',
                  })}
                >
                  {t('options.expires_after.suffix')}
                </span>
              </div>
            </div>
          </div>

          {showDaysError && (
            <p
              id={DAYS_ERROR_ID}
              role="alert"
              className={`body-text-4 mt-xs mb-0 ${css({
                color: palette.cayenne,
              })}`}
            >
              {t('options.expires_after.error')}
            </p>
          )}

          {showRichCallout && (
            <ImpactCallout
              checked={applyToGranted}
              onChange={setApplyToGranted}
              isPreviewLoading={isPreviewLoading}
              hasPreview={!!preview}
              isPreviewError={isPreviewError}
              formattedCount={formattedCount}
            />
          )}
        </div>

        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: PUBLISH_BANNER_BG,
            borderRadius: '4px',
            padding: '16px 24px',
            marginTop: '16px',
          })}
        >
          <span
            className={css({ color: palette.belizeHole, fontSize: '14px' })}
          >
            <strong>{publishBannerT('ready_go_live')}</strong>{' '}
            {publishBannerT('hit_publish')}
          </span>
          <Button
            type="button"
            isLoading={isSubmitting && !isModalOpen}
            disabled={
              !canManageMerchantSettings ||
              !hasChanges ||
              !isValid ||
              (applyToGranted && (isPreviewLoading || isPreviewError))
            }
            aria-label={
              !canManageMerchantSettings
                ? t('publish_requires_manage_aria')
                : undefined
            }
            onClick={handlePublishClick}
            overrides={{
              BaseButton: {
                style: {
                  width: '200px',
                  height: '48px',
                  paddingTop: '14px',
                  paddingBottom: '14px',
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px',
                  borderBottomLeftRadius: '4px',
                  borderBottomRightRadius: '4px',
                },
              },
            }}
          >
            {t('publish')}
          </Button>
        </div>
      </CmsPage>
      <ConfirmPublishModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={submit}
        isSubmitting={isSubmitting}
        formattedCount={formattedCount}
        days={days}
      />
    </>
  )
}

export default RewardExpiration
